import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import QRCode from 'qrcode';
import { Modal } from '../ui/Modal';
import { PrimaryButton, SecondaryButton } from '../../ui/common';
import { PhoneIcon, CheckIcon } from '../../ui/icons';
import { useTranslation } from '../../translations';
import { createScanSession, endScanSession, scanEventsUrl } from './scanApi';
import { signalScan } from './feedback';

// „Skanuj telefonem" — telefon jako bezprzewodowy czytnik dla okna na komputerze.
//
// Komputer pokazuje QR z adresem `/scan/<token>`. Telefon czyta go ZWYKŁYM aparatem (bez
// aplikacji), otwiera stronę ze skanerem i wysyła kody; tu przychodzą strumieniem (SSE) i trafiają
// do `onScan` — tego samego, co kod z czytnika USB, więc strona nie wie, skąd kod przyszedł.
//
// Po połączeniu telefonu okno ZWIJA SIĘ do paska w rogu: skanuje się po to, żeby coś zobaczyć
// na stronie (sztukę w magazynie, pozycję w rezerwacji), a otwarte okno by to zasłaniało.

const Steps = styled.ol`
  margin: 0 0 14px;
  padding-left: 20px;
  font-size: var(--fs-sm);
  color: var(--text);
  line-height: 1.6;
`;

const QrBox = styled.div`
  display: flex;
  justify-content: center;
  padding: 12px;
  background: #fff; /* QR musi mieć jasne tło niezależnie od motywu — ciemny kod na ciemnym się nie czyta */
  border-radius: var(--r-lg);
  border: 1px solid var(--input-border);

  img { width: 220px; height: 220px; image-rendering: pixelated; }
`;

const Status = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  padding: 10px 12px;
  border-radius: var(--r-md);
  font-size: var(--fs-sm);
  font-weight: 600;
  background: ${(p) => (p.$status === 'connected' ? 'var(--success-bg)' : p.$status === 'waiting' || p.$status === 'creating' ? 'var(--info-bg)' : 'var(--warning-bg)')};
  color: ${(p) => (p.$status === 'connected' ? 'var(--success)' : p.$status === 'waiting' || p.$status === 'creating' ? 'var(--info)' : 'var(--warning)')};
`;

const Warn = styled.p`
  margin: 12px 0 0;
  font-size: var(--fs-xs);
  color: var(--text-muted);
  line-height: 1.5;
`;

const LinkRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  margin-top: 10px;

  code {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: var(--fs-xs);
    color: var(--text-muted);
  }
`;

const Received = styled.ul`
  list-style: none;
  margin: 14px 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: var(--fs-sm);

  li { display: flex; gap: 8px; align-items: baseline; }
  strong { font-variant-numeric: tabular-nums; }
  span { color: var(--text-muted); overflow-wrap: anywhere; }
`;

const Bar = styled.div`
  position: fixed;
  right: 88px;
  bottom: calc(20px + var(--safe-bottom, 0px));
  z-index: var(--z-popover);
  display: flex;
  align-items: center;
  gap: 10px;
  max-width: min(460px, calc(100vw - 110px));
  padding: 10px 12px 10px 14px;
  border-radius: var(--r-pill);
  background: var(--card-bg);
  border: 1px solid var(--success-border);
  box-shadow: var(--shadow-lg);
  font-size: var(--fs-sm);

  .txt { min-width: 0; display: flex; flex-direction: column; }
  .txt b { color: var(--success); }
  .txt span { color: var(--text-muted); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  button { flex-shrink: 0; }

  @media (max-width: 640px) { right: 12px; left: 12px; max-width: none; }
`;

const MiniButton = styled.button`
  border: 1px solid var(--input-border);
  background: var(--panel-bg);
  color: var(--text);
  border-radius: var(--r-pill);
  padding: 6px 12px;
  font-size: var(--fs-xs);
  font-weight: 600;
  cursor: pointer;
`;

export default function PhonePairing({ open, onClose, onScan }) {
  const { t } = useTranslation();
  const [status, setStatus] = useState('creating'); // creating | waiting | connected | expired | error
  const [token, setToken] = useState(null);
  const [qr, setQr] = useState(null);
  const [received, setReceived] = useState([]);
  const [minimized, setMinimized] = useState(false);
  const [copied, setCopied] = useState(false);
  const onScanRef = useRef(onScan);
  const tokenRef = useRef(null);
  const esRef = useRef(null);

  useEffect(() => { onScanRef.current = onScan; }, [onScan]);

  const phoneUrl = token ? `${window.location.origin}/scan/${token}` : '';

  // Numer bieżącego uruchomienia. start() jest asynchroniczny, a StrictMode (i szybkie
  // zamknij/otwórz) odpala go dwa razy naraz: bez tego licznika przegrany przebieg zostawiał
  // otwarty strumień i żywą sesję, a jego zdarzenia nadpisywały stan zwycięzcy.
  const genRef = useRef(0);

  const stop = useCallback(() => {
    genRef.current += 1;
    if (esRef.current) { esRef.current.close(); esRef.current = null; }
    if (tokenRef.current) { endScanSession(tokenRef.current); tokenRef.current = null; }
  }, []);

  const start = useCallback(async () => {
    stop();
    const gen = genRef.current;
    const current = () => gen === genRef.current;
    setStatus('creating');
    setReceived([]);
    setMinimized(false);
    setQr(null);
    try {
      const s = await createScanSession();
      if (!current()) { endScanSession(s.token); return; }
      tokenRef.current = s.token;
      setToken(s.token);
      const qrUrl = await QRCode.toDataURL(`${window.location.origin}/scan/${s.token}`, { margin: 1, width: 440, errorCorrectionLevel: 'M' });
      if (!current()) return; // sesję zamknął już stop() przez tokenRef
      setQr(qrUrl);

      // Telefon, który otworzy link, zanim strumień się połączy, nie trafi do nikogo zdarzeniem
      // `phone` — dowiemy się o nim z `ready` (phoneConnected). Oba przypadki muszą więc zwijać
      // okno tak samo, a „Czekam na telefon” pokazujemy dopiero, gdy strumień naprawdę słucha.
      let connectedOnce = false;
      const markConnected = () => {
        setStatus('connected');
        if (!connectedOnce) { connectedOnce = true; setMinimized(true); }
      };

      const es = new EventSource(scanEventsUrl(s.token), { withCredentials: true });
      esRef.current = es;
      es.onmessage = async (msg) => {
        if (!current()) { es.close(); return; }
        let ev;
        try { ev = JSON.parse(msg.data); } catch (_) { return; }
        if (ev.type === 'ready') {
          if (ev.phoneConnected) markConnected();
          else if (!connectedOnce) setStatus('waiting');
        }
        if (ev.type === 'phone') markConnected();
        if (ev.type === 'code') {
          markConnected();
          let wynik;
          try { wynik = await onScanRef.current(ev.code); } catch (_) { wynik = { tone: 'error', text: t('common.error') }; }
          const tone = (wynik && wynik.tone) || 'ok';
          // Telefon już dał sygnał; tu piszczymy tylko przy problemie — przy komputerze stoi
          // często ktoś inny niż osoba z telefonem i to jego trzeba zawołać.
          if (tone !== 'ok') signalScan(tone);
          setReceived((prev) => [{ code: ev.code, text: wynik && wynik.text, tone, at: Date.now() }, ...prev].slice(0, 5));
        }
        if (ev.type === 'expired' || ev.type === 'ended' || ev.type === 'replaced') {
          setStatus('expired');
          setMinimized(false);
          es.close();
        }
      };
      // EventSource sam wznawia połączenie; zamknięte na stałe = serwer odmówił (sesja wygasła).
      es.onerror = () => {
        if (!current()) { es.close(); return; }
        if (es.readyState === EventSource.CLOSED) { setStatus('expired'); setMinimized(false); }
      };
    } catch (_) {
      if (current()) setStatus('error');
    }
  }, [stop, t]);

  useEffect(() => {
    if (open) start();
    return () => stop();
  }, [open]); // celowo tylko `open`: start() przy każdej zmianie `t` tworzyłby nową sesję

  if (!open) return null;

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(phoneUrl); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch (_) { /* brak uprawnień — link widać obok */ }
  };

  const last = received[0];
  const insecure = window.location.protocol !== 'https:';
  const localhost = /^(localhost|127\.|\[::1\])/.test(window.location.hostname);

  if (minimized && status === 'connected') {
    return (
      <Bar data-cy="phone-pairing-bar" role="status">
        <PhoneIcon />
        <div className="txt">
          <b>{t('scanner.phone.connected')}</b>
          <span data-cy="phone-pairing-last">
            {last ? (last.text || last.code) : t('scanner.phone.waitingForCodes')}
          </span>
        </div>
        <MiniButton type="button" onClick={() => setMinimized(false)} data-cy="phone-pairing-expand">
          {t('scanner.phone.show')}
        </MiniButton>
        <MiniButton type="button" onClick={onClose} data-cy="phone-pairing-disconnect">
          {t('scanner.phone.disconnect')}
        </MiniButton>
      </Bar>
    );
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={t('scanner.phone.title')}
      subtitle={t('scanner.phone.subtitle')}
      size="sm"
      data-cy="phone-pairing-modal"
      elevated /* ponad nakładką szczegółów rezerwacji — patrz CameraScanModal */
      footer={(
        <>
          {status === 'connected' && (
            <SecondaryButton type="button" onClick={() => setMinimized(true)} data-cy="phone-pairing-minimize">
              {t('scanner.phone.minimize')}
            </SecondaryButton>
          )}
          {(status === 'expired' || status === 'error') && (
            <SecondaryButton type="button" onClick={start} data-cy="phone-pairing-retry">
              {t('scanner.phone.newCode')}
            </SecondaryButton>
          )}
          <PrimaryButton type="button" onClick={onClose} data-cy="phone-pairing-close">
            {t('scanner.phone.finish')}
          </PrimaryButton>
        </>
      )}
    >
      <Steps>
        <li>{t('scanner.phone.step1')}</li>
        <li>{t('scanner.phone.step2')}</li>
        <li>{t('scanner.phone.step3')}</li>
      </Steps>

      <QrBox>
        {qr ? (
          <img src={qr} alt={t('scanner.phone.qrAlt')} data-cy="phone-pairing-qr" data-token={token || ''} />
        ) : (
          <div style={{ width: 220, height: 220 }} />
        )}
      </QrBox>

      <Status $status={status} data-cy="phone-pairing-status" data-status={status} role="status">
        {status === 'connected' && <CheckIcon size={16} />}
        {t(`scanner.phone.status.${status}`)}
      </Status>

      {phoneUrl && (
        <LinkRow>
          <code title={phoneUrl}>{phoneUrl}</code>
          <SecondaryButton type="button" onClick={copyLink} style={{ padding: '6px 12px', fontSize: 'var(--fs-xs)' }}>
            {copied ? t('scanner.phone.copied') : t('scanner.phone.copyLink')}
          </SecondaryButton>
        </LinkRow>
      )}

      {(insecure || localhost) && (
        <Warn data-cy="phone-pairing-dev-warning">
          {localhost ? t('scanner.phone.localhostWarning') : t('scanner.phone.httpWarning')}
        </Warn>
      )}

      {received.length > 0 && (
        <Received data-cy="phone-pairing-received">
          {received.map((r) => (
            <li key={r.at}>
              <strong>{r.code}</strong>
              {r.text && <span>{r.text}</span>}
            </li>
          ))}
        </Received>
      )}
    </Modal>
  );
}
